import { Eye } from 'lucide-react';
import type { EndUserListDto } from '../types/end-users.types';
import { statusLabelMap, statusVariantMap } from '../utils/end-users.utils';
import { Button } from '@/shared/components/ui/button';
import type { DataTableColumn, PaginationMeta } from '@/shared/components/ui/data-table';
import { DataTable } from '@/shared/components/ui/data-table';
import { StatusBadge } from '@/shared/components/ui/status-badge';

interface EndUsersTableProps {
  data: EndUserListDto[];
  pagination?: PaginationMeta;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onViewDetail?: (userId: string) => void;
}

export function EndUsersTable({
  data,
  pagination,
  onPageChange,
  onPageSizeChange,
  onViewDetail,
}: EndUsersTableProps) {
  const columns: DataTableColumn<EndUserListDto>[] = [
    {
      key: 'displayName',
      header: 'Nombre',
      render: (row) => (
        <span data-testid={`end-user-name-${row.id}`} className="text-light text-sm">
          {row.displayName ?? '—'}
        </span>
      ),
    },
    {
      key: 'email',
      header: 'Correo',
      render: (row) => (
        <span data-testid={`end-user-email-${row.id}`} className="text-primary text-sm">
          {row.email}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Estado de cuenta',
      render: (row) => (
        <div data-testid={`end-user-status-${row.id}`}>
          <StatusBadge status={statusVariantMap[row.status]} label={statusLabelMap[row.status]} />
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Fecha de registro',
      render: (row) => (
        <span data-testid={`end-user-created-${row.id}`} className="text-primary text-sm">
          {new Date(row.createdAt).toLocaleDateString('es-MX')}
        </span>
      ),
    },
    {
      key: 'workspaces',
      header: 'Espacios de trabajo',
      render: (row) => (
        <div
          data-testid={`end-user-workspaces-${row.id}`}
          className="flex flex-row flex-wrap gap-x-2 gap-y-1"
        >
          {row.workspaces.length === 0 ? (
            <span className="text-primary text-sm opacity-50">—</span>
          ) : (
            row.workspaces.map((ws, i) => (
              <span key={ws.tenantId} className="text-primary text-sm">
                {ws.tenantName}
                {i < row.workspaces.length - 1 ? ',' : ''}
              </span>
            ))
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Acciones',
      render: (row) => (
        <Button
          data-testid={`view-end-user-${row.id}`}
          iconImg={<Eye />}
          title="Ver detalle"
          onClick={() => onViewDetail?.(row.id)}
        />
      ),
    },
  ];

  return (
    <div data-testid="end-users-table">
      <DataTable
        title="Usuarios finales"
        subtitle={String(pagination?.totalCount ?? data.length)}
        rows={data}
        columns={columns}
        getRowKey={(row) => row.id}
        pagination={pagination}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
}
